import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Eye, Inbox } from "lucide-react";

const STATUS_COLOR = {
  pending: "bg-amber-50 text-amber-700",
  approved: "bg-emerald-50 text-emerald-700",
  rejected: "bg-rose-50 text-rose-700",
};

function fmt(iso) { return iso ? new Date(iso).toLocaleString() : "—"; }

export default function AdminSubscriptionPaymentsPage() {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/subscription-payments", { params: { status: statusFilter || undefined } });
      setItems(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const openReceipt = async (p) => {
    setViewing(p); setReceipt(null);
    try {
      const r = await api.get(`/admin/subscription-payments/${p.id}/receipt`);
      setReceipt(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approve = async (p) => {
    if (!window.confirm(`Approve payment of ₱${p.amount.toLocaleString()} for ${p.school_name}?`)) return;
    setBusy(true);
    try { await api.post(`/admin/subscription-payments/${p.id}/approve`); toast.success("Approved"); setViewing(null); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejectFor) return;
    setBusy(true);
    try {
      await api.post(`/admin/subscription-payments/${rejectFor.id}/reject`, { rejection_reason: reason.trim() || null });
      toast.success("Rejected");
      setRejectFor(null); setReason(""); setViewing(null); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <div className="space-y-5" data-testid="admin-subscription-payments-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs text-slate-400 uppercase font-heading">Billing</p>
            <h1 className="font-heading text-3xl font-bold">Subscription Payments</h1>
            <p className="text-sm text-brand-inkMuted mt-1">Review and approve subscription receipts uploaded by school owners.</p>
          </div>
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40" data-testid="payments-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? <p className="text-center text-slate-500 py-8">Loading…</p> : (
          items.length === 0 ? (
            <Card className="p-10 rounded-3xl text-center">
              <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <p className="font-heading font-bold">No payments {statusFilter || "yet"}</p>
            </Card>
          ) : (
            <Card className="rounded-3xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr><th className="text-left">School</th><th className="text-left">Plan</th><th className="text-left">Amount</th><th className="text-left">Reference</th><th className="text-left">Submitted</th><th className="text-left">Status</th><th></th></tr>
                  </thead>
                  <tbody>
                    {items.map((p) => (
                      <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                        <td><div className="font-semibold">{p.school_name}</div><div className="text-xs text-slate-500">{p.submitted_by_name}</div></td>
                        <td>{p.plan_name}</td>
                        <td>₱{(p.amount || 0).toLocaleString()}</td>
                        <td><span className="font-mono text-xs">{p.reference_number}</span></td>
                        <td className="text-xs text-slate-500">{fmt(p.submitted_at)}</td>
                        <td><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLOR[p.status]}`}>{p.status}</span></td>
                        <td>
                          <Button size="sm" variant="outline" onClick={() => openReceipt(p)} data-testid={`payment-view-${p.id}`}><Eye className="w-3.5 h-3.5 mr-1" /> Review</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )
        )}
      </div>

      <Dialog open={!!viewing} onOpenChange={(v) => { if (!v) { setViewing(null); setReceipt(null); } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="payment-review-dialog">
          <DialogHeader>
            <DialogTitle>{viewing?.school_name} — {viewing?.plan_name}</DialogTitle>
            <DialogDescription>Review the uploaded receipt and approve or reject the payment.</DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Amount:</span> <span className="font-semibold">₱{viewing.amount.toLocaleString()}</span></div>
                <div><span className="text-slate-500">Reference:</span> <span className="font-mono">{viewing.reference_number}</span></div>
                <div><span className="text-slate-500">Submitted by:</span> {viewing.submitted_by_name}</div>
                <div><span className="text-slate-500">When:</span> {fmt(viewing.submitted_at)}</div>
              </div>
              {viewing.note && <div className="text-sm bg-slate-50 rounded-xl p-2"><span className="text-slate-500 text-xs">Owner note:</span> {viewing.note}</div>}
              <div>
                <div className="text-xs text-slate-500 mb-1">Receipt</div>
                {!receipt && <p className="text-sm text-slate-500">Loading receipt…</p>}
                {receipt?.receipt_mime?.startsWith("image/") && (
                  <img src={receipt.receipt_base64} alt="receipt" className="max-h-96 rounded-2xl border border-slate-200" data-testid="payment-receipt-img" />
                )}
                {receipt?.receipt_mime === "application/pdf" && (
                  <a href={receipt.receipt_base64} download={`receipt-${viewing.reference_number}.pdf`} className="inline-block text-sm text-[#1D4ED8] hover:underline">Download PDF receipt</a>
                )}
              </div>
              {viewing.rejection_reason && <div className="text-sm bg-rose-50 text-rose-700 rounded-xl p-2"><span className="text-xs">Rejected:</span> {viewing.rejection_reason}</div>}
            </div>
          )}
          {viewing?.status === "pending" && (
            <DialogFooter className="gap-2">
              <Button variant="outline" className="text-rose-500" onClick={() => setRejectFor(viewing)} data-testid="payment-reject-btn"><XCircle className="w-4 h-4 mr-1" /> Reject</Button>
              <Button onClick={() => approve(viewing)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="payment-approve-btn"><CheckCircle2 className="w-4 h-4 mr-1" /> Approve</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectFor} onOpenChange={(v) => { if (!v) { setRejectFor(null); setReason(""); } }}>
        <DialogContent data-testid="payment-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject payment</DialogTitle>
            <DialogDescription>The reason you enter is sent to the school owner.</DialogDescription>
          </DialogHeader>
          <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (sent to the owner)…" data-testid="payment-reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button onClick={reject} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="payment-reject-confirm">Reject payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
